import { useState } from 'react'
import { Center, Group, Skeleton, Stack, Text } from '@mantine/core'
import { TaskCard } from './TaskCard'
import { ViewAllTasksLink } from './ViewAllTasksLink'
import { isTaskOpen } from './TaskCardHelpers'
import { Button } from '../Common/Button'
import { Checkbox } from '../Common/Checkbox'
import { StatusMessage } from '../Common/StatusMessage'
import { getEventReminderAvailability } from '../../utils/eventReminder'
import type { ClaimTask, TaskHoldValues } from '../../types/task'
import type { EventReminderFeedback } from '../../hooks/useEventReminder'

export interface TasksTabProps {
  tasks: ClaimTask[]
  loading?: boolean
  error?: string | null
  // Retries the tasks fetch when it failed.
  onRetry?: () => void
  // MC1 case id for the "View all Tasks" link.
  caseId: number
  // Puts a task on hold (On Hold Until date + description in one PATCH).
  onHoldTask?: (taskId: string, values: TaskHoldValues) => Promise<boolean>
  // Id of the task whose hold is currently in flight.
  holdingTaskId?: string | null
  // Message from the last failed hold, carrying the id of the card it belongs to.
  holdError?: { taskId: string; message: string } | null
  // Opens Outlook's New Event form for a task (MSD-684).
  onAddEventReminder?: (task: ClaimTask) => void
  // Id of the task whose event form is currently being opened.
  addingReminderTaskId?: string | null
  // Outcome of the last reminder attempt, carrying the id of the card it belongs to.
  reminderFeedback?: EventReminderFeedback | null
}

export function TasksTab({
  tasks,
  loading = false,
  error = null,
  onRetry,
  caseId,
  onHoldTask,
  holdingTaskId = null,
  holdError = null,
  onAddEventReminder,
  addingReminderTaskId = null,
  reminderFeedback = null,
}: Readonly<TasksTabProps>) {
  // Off on every visit: ClaimTabs does not keep panels mounted, so leaving the tab resets it.
  const [viewAll, setViewAll] = useState(false)
  // An Open task counts and is listed whether or not it is on hold — a hold parks the work
  // without closing it. Closed and Cancelled tasks are only listed once View All is ticked.
  const openTasks = tasks.filter(isTaskOpen)
  const visibleTasks = viewAll ? tasks : openTasks
  const pendingCount = openTasks.length
  const isEmpty = !loading && !error && visibleTasks.length === 0
  // While loading or after a failure there is no count to report — showing (0) would
  // read as 'no pending tasks' rather than 'not known yet'.
  const showCount = !loading && !error
  // The same for every card — the host, not the task, decides whether Outlook can open
  // a New Event form at all.
  const reminderAvailability = getEventReminderAvailability()

  return (
    <Stack gap="sm" flex={1}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs" c="dimmed">
          Pending Tasks{showCount ? ` (${pendingCount})` : ''}
        </Text>
        <ViewAllTasksLink caseId={caseId} />
      </Group>

      {!loading && !error && (
        <Checkbox
          label="View All"
          size="xs"
          checked={viewAll}
          onChange={(event) => setViewAll(event.currentTarget.checked)}
        />
      )}

      {loading && (
        <Stack gap={4}>
          <Skeleton height={56} radius="sm" />
          <Skeleton height={56} radius="sm" />
        </Stack>
      )}

      {!loading && error && (
        <Stack gap="xs">
          <StatusMessage status="error">{error}</StatusMessage>
          {onRetry && <Button label="Retry" size="xs" variant="outline" onClick={onRetry} />}
        </Stack>
      )}

      {/* flex={1} takes the rest of the panel height, so the message sits in its centre. */}
      {isEmpty && (
        <Center flex={1} py="xl">
          <Text fz="xs" c="dimmed" ta="center">
            No current tasks
          </Text>
        </Center>
      )}

      {!loading && !error && visibleTasks.length > 0 && (
        <Stack gap={4}>
          {visibleTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onHold={onHoldTask}
              saving={holdingTaskId === task.id}
              error={holdError?.taskId === task.id ? holdError.message : null}
              onAddEventReminder={onAddEventReminder}
              reminderDisabledReason={reminderAvailability.reason ?? null}
              addingReminder={addingReminderTaskId === task.id}
              reminderFeedback={reminderFeedback?.taskId === task.id ? reminderFeedback : null}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
